import BaseApi, { API_BASE_URL, ApiResponse } from './base';

export enum UserRole {
  LEARNER = 'learner',
  CREATOR = 'creator',
  ADMIN = 'admin',
}

export interface Department {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface File {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  provider: string;
  isMediaLibrary: boolean;
  createdAt: string;
}

export interface User {
  id: string;
  email: string;
  auth0UserId?: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  position?: string;
  positionDescription?: string;
  department?: Department;
  departmentId?: string;
  avatarFile?: File | null;
  avatarFileId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  position?: string;
  positionDescription?: string;
  role: UserRole;
  departmentId?: string;
  avatarFileId?: string;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  position?: string;
  positionDescription?: string;
  role?: UserRole;
  departmentId?: string | null;
  avatarFileId?: string | null;
}

export interface SlackSyncResult {
  totalSlackUsers: number;
  filteredOutUsers: number;
  validUsers: number;
  usersCreated: number;
  avatarsUpdated: number;
  errors: number;
}

class UsersApi extends BaseApi {
  constructor(baseURL: string = API_BASE_URL) {
    super(baseURL);
  }

  async getUsers(params?: {
    cursor?: string;
    limit?: number;
    departmentId?: string;
    role?: UserRole;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<User[]>> {
    return this.get<User[]>(`/users`, {
      params: params || {},
    });
  }

  async getUser(id: string): Promise<ApiResponse<User>> {
    return this.get<User>(`/users/${id}`);
  }

  async createUser(data: CreateUserRequest): Promise<ApiResponse<User>> {
    return this.post<User>('/users', data);
  }

  async updateUser(id: string, data: UpdateUserRequest): Promise<ApiResponse<User>> {
    return this.patch<User>(`/users/${id}`, data);
  }

  async deleteUser(id: string): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/users/${id}`);
  }

  async syncFromSlack(): Promise<ApiResponse<SlackSyncResult>> {
    return this.post<SlackSyncResult>('/users/sync-from-slack', {});
  }
}

export default new UsersApi();
